import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Aside, Main } from '@expo/html-elements';
import { Container, theme, useResponsive } from '@sg/garnish';

import { useIsLoggedIn, useIsLoggingIn } from '../../../context/AuthMachine';
import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';
import { AppFooter } from '../../AppFooter';
import { LoadingAnimation } from '../../LoadingAnimation';
import { PageWrapper } from '../../PageWrapper';
import { AccountMenu } from '../AccountMenu';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountScreenContainer = (props: AccountScreenContainerProps) => {
  const { screen: screenId } = props;

  const { currentBreakpoint } = useResponsive();
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <AccountSignedOutMessage redirectTo={screenId} />;
  }

  if (currentBreakpoint.isXS) {
    return <AccountScreenContainerXS {...props} />;
  }

  return <AccountScreenContainerSM {...props} />;
};

// ─── Sub Components ──────────────────────────────────────────────────────────

const AccountScreenContainerPageWrapper = (
  props: Pick<
    AccountScreenContainerProps,
    'children' | 'testID' | 'withScrollWrapper'
  >,
) => {
  const { testID, children, withScrollWrapper = true } = props;
  const { currentBreakpoint } = useResponsive();

  if (withScrollWrapper) {
    return (
      <PageWrapper
        testID={testID}
        showsVerticalScrollIndicator={false}
        style={styles.pageWrapper}
        withHeaderTitle
        withoutFooter={currentBreakpoint.isXS}
      >
        {children}
      </PageWrapper>
    );
  }

  return (
    <>
      <Main style={styles.pageWrapper} testID={testID}>
        {children}
      </Main>

      {currentBreakpoint.isXS ? null : <AppFooter />}
    </>
  );
};

const AccountScreenContainerSM = (props: AccountScreenContainerProps) => {
  const { screen: screenId, testID, children, withScrollWrapper } = props;
  const { minWidth, match } = useResponsive();

  const menuWrapperStyles = [
    styles.menuWrapper,
    match([{}, styles.menuWrapperSM, styles.menuWrapperMD]),
  ];

  const tabContentStyles = [
    styles.tabContent,
    minWidth.isLG ? styles.tabContentLG : {},
  ];

  return (
    <AccountScreenContainerPageWrapper
      testID={testID}
      withScrollWrapper={withScrollWrapper}
    >
      <Container
        customSpacing={0}
        wrapperStyle={styles.containerSM}
        style={styles.containerSM}
      >
        <View style={styles.contentWrapper}>
          <Aside style={menuWrapperStyles}>
            <AccountMenu screen={screenId} />
          </Aside>

          <View style={tabContentStyles}>{children}</View>
        </View>
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

const AccountScreenContainerXS = (props: AccountScreenContainerProps) => {
  const { testID, children, removeSpacing, withScrollWrapper } = props;

  return (
    <AccountScreenContainerPageWrapper
      testID={testID}
      withScrollWrapper={withScrollWrapper}
    >
      <Container
        removeSpacing={removeSpacing}
        wrapperStyle={styles.containerXS}
        style={styles.containerXS}
      >
        {children}
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentWrapper: {
    flex: 1,
    flexDirection: 'row',
  },
  containerXS: {
    flex: 1,
    minHeight: '100%',
  },
  containerSM: {
    flex: 1,
    minHeight: 700,
  },
  tabContent: {
    flex: 1,
    minHeight: '100%',
    paddingTop: theme.spacing['12'],
    paddingHorizontal: theme.spacing['10'],
  },
  tabContentLG: {
    paddingHorizontal: theme.spacing['20'],
  },
  menuWrapper: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
  },
  menuWrapperSM: {
    width: 280,
  },
  menuWrapperMD: {
    width: 320,
  },
});

// ────────────────────────────────────────────────────────────────────────────────

export type AccountScreenContainerProps = Readonly<{
  screen: AccountTabScreenId;
  withScrollWrapper?: boolean;
  removeSpacing?: boolean;
}> &
  ViewProps;
